import { useId, useMemo } from "react";
import { ospThemes } from "@ospweb/sdk-ui";
import type { ChartAnnotationPoint } from "../types";
import { cn } from "utils";

interface MiniSparklineChartProps {
  points: number[];
  direction?: "up" | "down" | "flat";
  annotation?: ChartAnnotationPoint;
  width?: number;
  height?: number;
  className?: string;
}

interface ChartCoord {
  x: number;
  y: number;
}

const PADDING_X = 4;
const PADDING_BOTTOM = 4;
const ANNOTATION_TOP_PADDING = 20;
const ANNOTATION_CIRCLE_R = 4;
const CURVE_STROKE_WIDTH = 1.6;
const ANNOTATION_CIRCLE_STROKE_WIDTH = 2;
/** 渐变与描边均不进入标注空心圆内部 */
const ANNOTATION_MASK_R = ANNOTATION_CIRCLE_R;
const CURVE_TENSION = 0.42;
/** 渐变向下延伸超出基线的距离（px），使淡出更缓、视觉更长 */
const GRADIENT_EXTEND_Y = 3;

/** Catmull-Rom → cubic Bézier，与 KlineColumn 的 curveCatmullRom 视觉接近 */
const buildSmoothCurvePath = (
  coords: ChartCoord[],
  tension = CURVE_TENSION,
): string => {
  if (coords.length < 2) {
    return "";
  }
  if (coords.length === 2) {
    return `M ${coords[0].x} ${coords[0].y} L ${coords[1].x} ${coords[1].y}`;
  }

  let path = `M ${coords[0].x.toFixed(2)} ${coords[0].y.toFixed(2)}`;

  for (let index = 0; index < coords.length - 1; index += 1) {
    const p0 = coords[Math.max(index - 1, 0)];
    const p1 = coords[index];
    const p2 = coords[index + 1];
    const p3 = coords[Math.min(index + 2, coords.length - 1)];

    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

    path += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }

  return path;
};

const buildAreaPath = (
  curvePath: string,
  coords: ChartCoord[],
  baselineY: number,
): string => {
  if (!curvePath || coords.length < 2) {
    return "";
  }
  const last = coords[coords.length - 1];
  const first = coords[0];
  return `${curvePath} L ${last.x.toFixed(2)} ${baselineY} L ${first.x.toFixed(2)} ${baselineY} Z`;
};

const toChartCoords = (
  points: number[],
  width: number,
  height: number,
  paddingTop: number,
  direction: "up" | "down" | "flat",
): ChartCoord[] => {
  if (points.length === 0) {
    return [];
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const innerW = width - PADDING_X * 2;
  const innerH = height - paddingTop - PADDING_BOTTOM;

  return points.map((value, index) => {
    const ratio = (value - min) / range;
    // 跌：仅镜像曲线走向，填充与渐变仍向图表底部延伸
    const y =
      direction === "down"
        ? paddingTop + ratio * innerH
        : paddingTop + (1 - ratio) * innerH;

    return {
      x: PADDING_X + (index / Math.max(1, points.length - 1)) * innerW,
      y,
    };
  });
};

/**
 * 轻量 sparkline：平滑曲线 + 曲线下方向下渐变 + 空心标注圆 + 气泡文案。
 * 涨/跌仅换色与 Y 轴映射；填充区始终在曲线下方，不做整图翻转。
 */
export const MiniSparklineChart: React.FC<MiniSparklineChartProps> = ({
  points,
  direction = "up",
  annotation,
  width = 113,
  height = 53,
  className,
}) => {
  const gradientId = useId().replace(/:/g, "");
  const annotationMaskId = useId().replace(/:/g, "");
  const paddingTop = annotation ? ANNOTATION_TOP_PADDING : PADDING_X;
  const baselineY = height - PADDING_BOTTOM;

  const lineColor =
    direction === "down"
      ? ospThemes.colors.content_accent2_primary
      : ospThemes.colors.content_accent1_primary;

  const { areaPath, strokePath, annotationPoint, gradientY1, gradientY2 } =
    useMemo(() => {
      const coords = toChartCoords(
        points,
        width,
        height,
        paddingTop,
        direction,
      );
      const fullCurve = buildSmoothCurvePath(coords);
      const fillBaselineY = baselineY + GRADIENT_EXTEND_Y;
      const curveTopY = Math.min(...coords.map((c) => c.y));

      return {
        areaPath: buildAreaPath(fullCurve, coords, fillBaselineY),
        strokePath: fullCurve,
        gradientY1: curveTopY,
        gradientY2: fillBaselineY,
        annotationPoint:
          annotation && coords[annotation.pointIndex]
            ? coords[annotation.pointIndex]
            : null,
      };
    }, [annotation, baselineY, direction, height, paddingTop, points, width]);

  if (points.length < 2) {
    return null;
  }

  const isDown = direction === "down";

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width, height }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="size-full overflow-visible"
        aria-hidden
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="0"
            y1={gradientY1}
            x2="0"
            y2={gradientY2}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
          {annotationPoint ? (
            <mask id={annotationMaskId}>
              <rect width={width} height={height} fill="white" />
              <circle
                cx={annotationPoint.x}
                cy={annotationPoint.y}
                r={ANNOTATION_MASK_R}
                fill="black"
              />
            </mask>
          ) : null}
        </defs>

        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
          mask={annotationPoint ? `url(#${annotationMaskId})` : undefined}
        />
        <path
          d={strokePath}
          fill="none"
          stroke={lineColor}
          strokeWidth={CURVE_STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          mask={annotationPoint ? `url(#${annotationMaskId})` : undefined}
        />

        {annotation && annotationPoint ? (
          <g>
            <foreignObject
              x={Math.max(0, annotationPoint.x - 36)}
              y={Math.max(0, annotationPoint.y - 22)}
              width={72}
              height={16}
            >
              <div className="flex justify-center">
                <span
                  className={cn(
                    "inline-flex items-center rounded-1 px-1 py-0.5 body_medium_xs whitespace-nowrap",
                    isDown
                      ? "bg-background_accent2_overlay20 text-content_accent2_primary"
                      : "bg-background_accent1_overlay20 text-content_accent1_primary",
                  )}
                >
                  {annotation.label}
                </span>
              </div>
            </foreignObject>
            <circle
              cx={annotationPoint.x}
              cy={annotationPoint.y}
              r={ANNOTATION_CIRCLE_R}
              fill="none"
              stroke={lineColor}
              strokeWidth={ANNOTATION_CIRCLE_STROKE_WIDTH}
            />
          </g>
        ) : null}
      </svg>
    </div>
  );
};
